export const SODIOHOME_DEVICE_TYPES = [
  { id: 3, name: 'Growatt' },
  { id: 4, name: 'inesco 12K - WR Hybrid' }
] as const;

export const getDeviceTypeName = (deviceId: number): string =>
  SODIOHOME_DEVICE_TYPES.find(d => d.id === deviceId)?.name ?? '';

// [inverter][cluster] = batteryCount
export type PresetConfig = number[][];

// 3D array: [inverter][cluster][batteryIndex] = serialNumber
export type BatterySnTree = string[][][];

export const INSTALLATION_PRESETS: Record<string, PresetConfig> = {
  'sodistore home 9': [[1, 1]],
  'sodistore home 18': [[2, 2]],
  'sodistore home 27': [[2, 2], [1, 1]],
  'sodistore home 36': [[2, 2], [2, 2]],
};

export const buildSodistoreProPreset = (inverterCount: number): PresetConfig =>
  Array.from({ length: inverterCount }, () => [2, 2]);

export const parseSodistoreProInverterCount = (model: string): number => {
  const n = parseInt(model, 10);
  return isNaN(n) || n < 1 ? 1 : n;
};

export const buildEmptyTree = (preset: PresetConfig): BatterySnTree => {
  return preset.map((inv) =>
    inv.map((batteryCount) => Array.from({ length: batteryCount }, () => ''))
  );
};

export const parseBatterySnTree = (
  raw: string,
  preset: PresetConfig
): BatterySnTree => {
  if (!raw || raw.trim() === '') {
    return buildEmptyTree(preset);
  }

  const isStructured = raw.includes('/') || raw.includes('|');

  if (isStructured) {
    const inverters = raw.split('/');
    return preset.map((invPreset, invIdx) => {
      const clusterStr = inverters[invIdx] || '';
      const clusters = clusterStr ? clusterStr.split('|') : [];
      return invPreset.map((batteryCount, clIdx) => {
        const batteries = clusters[clIdx]
          ? clusters[clIdx].split(',').map((s) => s.trim())
          : [];
        return Array.from({ length: batteryCount }, (_, i) => batteries[i] || '');
      });
    });
  }

  // Legacy flat format: distribute by preset layout
  const allSns = raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '');
  let idx = 0;
  return preset.map((inv) =>
    inv.map((batteryCount) =>
      Array.from({ length: batteryCount }, () => allSns[idx++] || '')
    )
  );
};

export const serializeBatterySnTree = (tree: BatterySnTree): string => {
  return tree
    .map((inv) => inv.map((cluster) => cluster.join(',')).join('|'))
    .join('/');
};

export const remapTree = (
  oldTree: BatterySnTree,
  newPreset: PresetConfig
): BatterySnTree => {
  return newPreset.map((inv, invIdx) =>
    inv.map((batteryCount, clIdx) =>
      Array.from(
        { length: batteryCount },
        (_, batIdx) => oldTree[invIdx]?.[clIdx]?.[batIdx] || ''
      )
    )
  );
};

export const computeFlatValues = (
  preset: PresetConfig,
  tree: BatterySnTree
) => {
  const totalBatteries = preset.flat().reduce((a, b) => a + b, 0);
  const totalClusters = preset.reduce((sum, inv) => sum + inv.length, 0);
  return {
    batteryNumber: totalBatteries,
    batteryClusterNumber: totalClusters,
    batterySerialNumbers: serializeBatterySnTree(tree),
  };
};

export const wouldLoseData = (
  oldTree: BatterySnTree,
  newPreset: PresetConfig
): boolean => {
  for (let invIdx = 0; invIdx < oldTree.length; invIdx++) {
    for (let clIdx = 0; clIdx < (oldTree[invIdx] || []).length; clIdx++) {
      for (
        let batIdx = 0;
        batIdx < (oldTree[invIdx][clIdx] || []).length;
        batIdx++
      ) {
        const sn = oldTree[invIdx][clIdx][batIdx];
        if (sn && sn.trim() !== '') {
          if (invIdx >= newPreset.length) return true;
          if (clIdx >= (newPreset[invIdx] || []).length) return true;
          const newBatCount = newPreset[invIdx]?.[clIdx] ?? 0;
          if (batIdx >= newBatCount) return true;
        }
      }
    }
  }
  return false;
};
